import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import sorguOneriService, {
    SorguOneriGecis,
    SorguOneriRow
} from "@/services/sorguOneriService";

/**
 * Haciz önerisi kutusunun durumu (F2-İP5, DEV-2952).
 *
 * Aynı liste iki yerde gösteriliyor: İşlemler sayfasındaki panel ve üst çubuktaki
 * rozet (Ö1 kararı). Tek kaynaktan beslensinler diye liste burada tutulur, iki
 * bileşen de aynı state'e bakar — aksi halde "yok say" birinde işlenip diğerinde
 * kalırdı.
 */
export interface SorguOneriState {
    oneriler: Array<SorguOneriRow>;
    yukleniyor: boolean;
    /** Son yükleme hatası (kullanıcıya gösterilen sabit metin). Yeniden yüklemeyi ENGELLEMEZ. */
    hata: string | null;
    /** En az bir kez yükleme denendi mi (başarılı ya da başarısız). */
    denendiMi: boolean;
    /**
     * Sihirbaz köprüsü için ayrılan öneri. `TopluHacizHazirlaView` sayfaya
     * girdiğinde bunu okur; sayfa yenilenirse (state kaybolur) uçtan tekrar
     * çekilir, o yüzden bu alan yalnız hızlı yol / gösterim kolaylığıdır.
     */
    koprulenenOneri: SorguOneriRow | null;
}

/**
 * Fabrika olarak yazildi: modul birden cok store'a kaydedilirse (birim testlerinde
 * her `it` icin taze store kurulur) durumun paylasilmamasi gerekiyor.
 */
const state = (): SorguOneriState => ({
    oneriler: [],
    yukleniyor: false,
    hata: null,
    denendiMi: false,
    koprulenenOneri: null
});

const mutations: MutationTree<SorguOneriState> = {
    SET_ONERILER(state, oneriler: Array<SorguOneriRow>) {
        state.oneriler = oneriler;
    },
    SET_YUKLENIYOR(state, yukleniyor: boolean) {
        state.yukleniyor = yukleniyor;
    },
    SET_HATA(state, hata: string | null) {
        state.hata = hata;
    },
    SET_DENENDI(state, denendiMi: boolean) {
        state.denendiMi = denendiMi;
    },
    /** Listeden çıkarır — kapanan öneri (yok sayıldı / görev oluşturuldu) artık açık değildir. */
    ONERI_CIKAR(state, id: number) {
        state.oneriler = state.oneriler.filter((oneri) => oneri.id !== id);
    },
    /** Açık kalan bir durum geçişini (yeni → görüldü) yerinde günceller. */
    ONERI_DURUM_YAZ(state, payload: { id: number; durum: SorguOneriGecis }) {
        const oneri = state.oneriler.find((o) => o.id === payload.id);
        if (oneri) {
            oneri.durum = payload.durum;
        }
    },
    SET_KOPRULENEN(state, oneri: SorguOneriRow | null) {
        state.koprulenenOneri = oneri;
    }
};

const getters: GetterTree<SorguOneriState, any> = {
    /** Uç zaten yalnız açık önerileri döndürüyor; state'te tutulan liste odur. */
    acikOneriler: (state) => state.oneriler,
    oneriSayisi: (state) => state.oneriler.length,
    yuksekOncelikliSayisi: (state) =>
        state.oneriler.filter((oneri) => oneri.oncelik === "yuksek").length,
    oneriYukleniyor: (state) => state.yukleniyor,
    oneriDenendiMi: (state) => state.denendiMi,
    oneriHatasi: (state) => state.hata,
    koprulenenOneri: (state) => state.koprulenenOneri
};

const actions: ActionTree<SorguOneriState, any> = {
    /**
     * Açık önerileri çeker.
     *
     * Tek koruma eşzamanlılıktır: panel ile üst çubuk rozeti aynı anda mount
     * olduğunda ikinci çağrı süren isteğe katılır, ikinci istek atılmaz.
     *
     * Hata KALICI OLARAK yükleme yapmayı engellemez: geçici bir 401/500 yüzünden
     * liste oturum boyunca donup kalmamalı. Yükleme yalnız kullanıcı bir sayfaya
     * girdiğinde ya da "Yenile" dediğinde tetikleniyor, yani döngü riski yok.
     */
    async yukle({commit, state}) {
        if (state.yukleniyor) return state.oneriler;

        commit("SET_YUKLENIYOR", true);
        try {
            const oneriler = await sorguOneriService.listele();
            commit("SET_ONERILER", oneriler);
            commit("SET_HATA", null);
            return oneriler;
        } catch (e: any) {
            // Kullanıcıya gösterilecek metin SABİT ve Türkçedir: ham axios mesajı
            // ("Request failed with status code 404") hem İngilizce hem de axios
            // yanıt interceptor'ının gösterdiği global uyarıyla çakışıyor.
            console.error("Haciz önerileri alınamadı:", e);
            commit("SET_HATA", "Haciz önerileri alınamadı.");
            return state.oneriler;
        } finally {
            commit("SET_DENENDI", true);
            commit("SET_YUKLENIYOR", false);
        }
    },

    /**
     * Durum geçişi. `goruldu` açık bir durumdur, kart listede kalır; `yok_sayildi`
     * ve `gorev_olusturuldu` kapalıdır ve kart listeden düşer (F2-K8: sayfa
     * yenilenince de geri gelmez, uç açık olmayanları döndürmüyor).
     */
    async durumGuncelle({commit}, payload: { id: number; durum: SorguOneriGecis }) {
        const guncel = await sorguOneriService.durumGuncelle(payload.id, payload.durum);
        if (payload.durum === "goruldu") {
            commit("ONERI_DURUM_YAZ", payload);
        } else {
            commit("ONERI_CIKAR", payload.id);
        }
        return guncel;
    },

    async yokSay({dispatch}, id: number) {
        return await dispatch("durumGuncelle", {id, durum: "yok_sayildi"});
    },

    async gorevOlusturulduIsaretle({dispatch}, id: number) {
        return await dispatch("durumGuncelle", {id, durum: "gorev_olusturuldu"});
    },

    koprulenenAyarla({commit}, oneri: SorguOneriRow | null) {
        commit("SET_KOPRULENEN", oneri);
    },

    koprulenenTemizle({commit}) {
        commit("SET_KOPRULENEN", null);
    }
};

const sorguOneri: Module<SorguOneriState, any> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};

export default sorguOneri;
